import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Import report components
import DailySalesReport from './DailySalesReport';
import MonthlySalesReport from './MonthlySalesReport';
import SalesReport from './SalesReport';
import InventoryReport from './InventoryReport';
import CustomerReport from './CustomerReport';
import IndividualCustomerReport from './IndividualCustomerReport';
import PaymentMethodsReport from './PaymentMethodsReport';
import PaymentsReceivedReport from './PaymentsReceivedReport';
import InsuranceReceivedReport from './InsuranceReceivedReport';
import DueEyeExamReport from './DueEyeExamReport';
import RxDueSoonReport from './RxDueSoonReport';
import RxExpiredReport from './RxExpiredReport';
import ExportButtons from './ExportButtons';

// Import utilities
import ReportFilters from './ReportFilters';
import { reportTypes } from './reportTypes';
import { reportingService } from '../../service/reportingService';

import EditInvoiceDialog from '../customerrecord/tabs/invoice/newInvoice/editInvoiceDialog';
import useDialogModal from 'hooks/useDialogModal';
import ReturnInvoiceDialog from 'components/customerrecord/tabs/invoice/newInvoice/returnInvoiceDialog';
import DispenseReport from './DispenseReport';
import PendingInsuranceClaimReport from './PendingInsuranceClaimReport';

function Reporting() {
  const [selectedReport, setSelectedReport] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null); // State to hold customer data for the dialog
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    datePreset: 'daily',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    locationId: null, // Changed to null to work with SelectLocation component
    invoiceType: 'sales',
    customerId: null, // For individual customer reports
    rxType: 'glasses',
    type: '',
    page: 1, // For pagination
    limit: 50 // Records per page
  });
  const [EditInvoiceDialogComp, showEditInvoiceDialogComp] = useDialogModal(EditInvoiceDialog);
  const [ReturnInvoiceDialogComp, showReturnInvoiceDialogComp] = useDialogModal(ReturnInvoiceDialog);

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    if ((selectedReport === 'sales' || selectedReport === 'customers' || selectedReport === 'pending-insurance-claims' || selectedReport === 'payment-methods' || selectedReport === 'payments-received' || selectedReport === 'insurance-received' || selectedReport === 'due-eye-exam' || selectedReport === 'rx-due-soon' || selectedReport === 'rx-expired') && !filters.locationId) {
      setError(`Please select a location for the ${selectedReport === 'sales' ? 'sales' : selectedReport === 'customers' ? 'customer' : selectedReport === 'pending-insurance-claims' ? 'pending insurance claims' : selectedReport === 'payment-methods' ? 'payment methods' : selectedReport === 'payments-received' ? 'payments received' : selectedReport === 'insurance-received' ? 'insurance received' : selectedReport === 'due-eye-exam' ? 'due eye exam' : selectedReport === 'rx-due-soon' ? 'rx due soon' : 'rx expired'} report.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      switch (selectedReport) {
        case 'sales':
          response = await reportingService.getInvoiceSalesReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.invoiceType || 'sales',
            filters.page,
            filters.limit
          );
          break;
        case 'dispense':
          response = await reportingService.getDispenseReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'pending-insurance-claims':
          response = await reportingService.getPendingInsuranceClaimsReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'daily-sales':
          response = await reportingService.getDailySalesReport(
            filters.date,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'monthly-sales':
          response = await reportingService.getMonthlySalesReport(filters.year, filters.month, filters.locationId, filters.page, filters.limit);
          break;
        case 'inventory':
          response = await reportingService.getInventoryReport(filters.type);
          break;
        case 'customers':
          response = await reportingService.getCustomerReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId
          );
          break;
        case 'individual-customer':
          if (!filters.customerId) {
            throw new Error('Please select a customer for individual customer report');
          }
          response = await reportingService.getIndividualCustomerReport(
            filters.customerId,
            filters.startDate,
            filters.endDate
          );
          break;
        case 'payment-methods':
          response = await reportingService.getPaymentMethodsReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId
          );
          break;
        case 'payments-received':
          response = await reportingService.getPaymentsReceivedReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'insurance-received':
          response = await reportingService.getInsuranceReceivedReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'due-eye-exam':
          response = await reportingService.getDueEyeExamReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.page,
            filters.limit
          );
          break;
        case 'rx-due-soon':
          response = await reportingService.getRxDueSoonReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.rxType || 'glasses',
            filters.page,
            filters.limit
          );
          break;
        case 'rx-expired':
          response = await reportingService.getRxExpiredReport(
            filters.dateFrom,
            filters.dateTo,
            filters.locationId,
            filters.rxType || 'glasses',
            filters.page,
            filters.limit
          );
          break;
        default:
          throw new Error('Invalid report type selected');
      }

      // The httpService already handles response.code checking through interceptors
      // If we get here, the response was successful (code === 0)
      setReportData(response.data || response);
    } catch (err) {
      console.error('API Error:', err);

      // The httpService already shows toast notifications for errors
      // We just need to set a local error state for component-specific handling
      if (err.response && err.response.status === 200 && err.response.data) {
        // This is a logical error (code: 1) that was caught by httpService
        setError(err.response.data.message || 'Report generation failed');
      } else {
        // This is a network or other error
        setError('Unable to generate report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    if (selectedReport === 'sales') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getInvoiceSalesReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.invoiceType || 'sales',
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'dispense') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getDispenseReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'monthly-sales') {
      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getMonthlySalesReport(
          filters.year,
          filters.month,
          filters.locationId,
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'payments-received') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getPaymentsReceivedReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'insurance-received') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getInsuranceReceivedReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'due-eye-exam') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getDueEyeExamReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'rx-due-soon') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getRxDueSoonReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.rxType || 'glasses',
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'rx-expired') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));

      setLoading(true);
      try {
        const response = await reportingService.getRxExpiredReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.rxType || 'glasses',
          page,
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLimitChange = async (newLimit) => {
    if (selectedReport === 'sales') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when changing limit

      setLoading(true);
      try {
        const response = await reportingService.getInvoiceSalesReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.invoiceType || 'sales',
          1, // Always go to page 1 when changing limit
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'dispense') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getDispenseReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'monthly-sales') {
      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when changing limit

      setLoading(true);
      try {
        const response = await reportingService.getMonthlySalesReport(
          filters.year,
          filters.month,
          filters.locationId,
          1, // Always go to page 1 when changing limit
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'payments-received') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getPaymentsReceivedReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'insurance-received') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getInsuranceReceivedReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'due-eye-exam') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getDueEyeExamReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'rx-due-soon') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getRxDueSoonReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.rxType || 'glasses',
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'rx-expired') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));

      setLoading(true);
      try {
        const response = await reportingService.getRxExpiredReport(
          filters.dateFrom,
          filters.dateTo,
          filters.locationId,
          filters.rxType || 'glasses',
          1,
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    switch (selectedReport) {
      case 'sales':
        return <SalesReport
          data={reportData}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
          onInvoiceClick={(invoice) => {
            setCustomer({ customer: invoice.customer || null });
            showEditInvoiceDialogComp(null, {
              invoiceId: invoice.id,
              onOpenReturnInvoice: (returnInvoiceId) => showReturnInvoiceDialogComp(null, { invoiceData: { id: returnInvoiceId } })
            });
          }}
        />;
      case 'dispense':
        return <DispenseReport
          data={reportData}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
          onInvoiceClick={(invoice) => {
            setCustomer({ customer: invoice.customer || null });
            showEditInvoiceDialogComp(null, {
              invoiceId: invoice.id,
              onOpenReturnInvoice: (returnInvoiceId) => showReturnInvoiceDialogComp(null, { invoiceData: { id: returnInvoiceId } })
            });
          }}
        />;
      case 'pending-insurance-claims':
        return <PendingInsuranceClaimReport
          data={reportData}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
          onInvoiceClick={(claim) => {
            if (!claim?.invoiceId) {
              setError('Unable to open invoice details for this claim.');
              return;
            }

            setCustomer(null);
            showEditInvoiceDialogComp(null, {
              invoiceId: claim.invoiceId,
              onOpenReturnInvoice: (returnInvoiceId) => showReturnInvoiceDialogComp(null, { invoiceData: { id: returnInvoiceId } })
            });
          }}
        />;
      case 'daily-sales':
        return <DailySalesReport
          data={reportData}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
        />;
      case 'monthly-sales':
        return <MonthlySalesReport
          data={reportData}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
        />;
      case 'inventory':
        return <InventoryReport data={reportData} />;
      case 'customers':
        return <CustomerReport data={reportData} />;
      case 'individual-customer':
        return <IndividualCustomerReport data={reportData} />;
      case 'payment-methods':
        return <PaymentMethodsReport data={reportData} />;
      case 'payments-received':
        return <PaymentsReceivedReport
          data={reportData}
          onPageChange={handlePageChange}
        />;
      case 'insurance-received':
        return <InsuranceReceivedReport
          data={reportData}
          onPageChange={handlePageChange}
        />;
      case 'due-eye-exam':
        return <DueEyeExamReport
          data={reportData}
          onPageChange={handlePageChange}
        />;
      case 'rx-due-soon':
        return <RxDueSoonReport
          data={reportData}
          onPageChange={handlePageChange}
        />;
      case 'rx-expired':
        return <RxExpiredReport
          data={reportData}
          onPageChange={handlePageChange}
        />;
      default:
        return <Typography>Report content coming soon...</Typography>;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reporting Dashboard
      </Typography>

      {/* Report Type Selection */}
      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="report-type-label">Select Report Type</InputLabel>
          <Select
            labelId="report-type-label"
            id="report-type-select"
            value={selectedReport}
            label="Select Report Type"
            onChange={(e) => {
              const nextReport = e.target.value;
              const today = new Date();
              const thirtyDaysOut = new Date(today);
              thirtyDaysOut.setDate(today.getDate() + 30);
              const toIsoDate = (value) => value.toISOString().split('T')[0];

              setSelectedReport(nextReport);
              setReportData(null); // Clear previous report data
              setError(null); // Clear any previous errors
              // Reset location filter and set rx defaults when changing report types
              setFilters(prev => ({
                ...prev,
                locationId: null,
                page: 1,
                rxType: 'glasses',
                dateFrom: nextReport === 'rx-due-soon' ? toIsoDate(today) : prev.dateFrom,
                dateTo: nextReport === 'rx-due-soon' ? toIsoDate(thirtyDaysOut) : prev.dateTo
              }));
            }}
          >
            {reportTypes.map((report) => (
              <MenuItem key={report.id} value={report.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: report.color, display: 'flex' }}>
                    <report.icon fontSize="small" />
                  </Box>
                  <Typography variant="body2">{report.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedReport && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {reportTypes.find((report) => report.id === selectedReport)?.description}
          </Typography>
        )}
      </Box>

      {/* Filters */}
      <ReportFilters
        selectedReport={selectedReport}
        filters={filters}
        setFilters={setFilters}
      />

      {/* Generate Button */}
      {selectedReport && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={
              loading ||
              ((selectedReport === 'sales' || selectedReport === 'customers' || selectedReport === 'payment-methods' || selectedReport === 'payments-received' || selectedReport === 'insurance-received' || selectedReport === 'due-eye-exam' || selectedReport === 'rx-due-soon' || selectedReport === 'rx-expired') && !filters.locationId)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>

          {reportData && (
            <ExportButtons
              reportType={selectedReport}
              reportData={reportData}
              filters={filters}
              onExportStart={(format) => {
                console.log(`Starting ${format} export...`);
              }}
              onExportComplete={(format, filename) => {
                console.log(`${format} export completed: ${filename}`);
              }}
              onExportError={(format, error) => {
                setError(`Export failed: ${error}`);
              }}
              disabled={loading}
            />
          )}

          {selectedReport === 'sales' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the sales report
            </Typography>
          )}

          {selectedReport === 'customers' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the customer report
            </Typography>
          )}

          {selectedReport === 'payment-methods' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the payment methods report
            </Typography>
          )}

          {selectedReport === 'payments-received' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the payments received report
            </Typography>
          )}

          {selectedReport === 'insurance-received' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the insurance received report
            </Typography>
          )}

          {selectedReport === 'due-eye-exam' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the due eye exam report
            </Typography>
          )}

          {selectedReport === 'rx-due-soon' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the Rx due soon report
            </Typography>
          )}

          {selectedReport === 'rx-expired' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the Rx expired report
            </Typography>
          )}
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Report Content */}
      {reportData && (
        <Paper sx={{ p: 3 }}>
          {renderReportContent()}
        </Paper>
      )}

      <EditInvoiceDialogComp ignoreBackdropClick={true} />
      <ReturnInvoiceDialogComp ignoreBackdropClick={true} customer={customer} />
    </Box>
  );
}

export default Reporting;
